/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";

import { memo, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useAgentContext } from "../../contexts/AgentContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";

import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import { config } from "../../config/runtime-config";
import "./control-tray.scss";

/**
 * Force audio output to use the loudspeaker (not earpiece) on mobile devices
 * This prevents the audio from switching between earpiece and loudspeaker
 */
const forceAudioToLoudspeaker = async (audioElement?: HTMLAudioElement): Promise<void> => {
  try {
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (!isMobile) {
      console.log('🔊 [Speaker] Not a mobile device, skipping loudspeaker enforcement');
      return;
    }

    console.log('🔊 [Speaker] Attempting to force audio to loudspeaker...');

    // Method 1: Use setSinkId if available (Chrome/Edge on Android)
    if (audioElement && 'setSinkId' in audioElement) {
      try {
        // Empty string means default output (loudspeaker on mobile)
        await (audioElement as any).setSinkId('');
        console.log('✅ [Speaker] setSinkId successful - audio routed to loudspeaker');
      } catch (sinkError) {
        console.warn('⚠️ [Speaker] setSinkId failed:', sinkError);
      }
    }

    // Method 2: Create a hidden audio element to lock the audio routing to loudspeaker
    if (!document.getElementById('audio-routing-anchor')) {
      const audioAnchor = document.createElement('audio');
      audioAnchor.id = 'audio-routing-anchor';
      audioAnchor.setAttribute('playsinline', 'true');
      audioAnchor.setAttribute('webkit-playsinline', 'true');
      audioAnchor.volume = 0.01; // Very low volume

      // Create a silent audio buffer
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0; // Silent

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Start the oscillator (this helps route audio to loudspeaker)
      oscillator.start();

      console.log('✅ [Speaker] Created audio routing anchor');

      // Keep the context alive
      setTimeout(() => {
        audioContext.close();
      }, 1000);
    }

    // Method 3: For iOS - set audio session category
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      console.log('📱 [Speaker] iOS detected - audio will use loudspeaker by default in web apps');
    }

    console.log('✅ [Speaker] Loudspeaker enforcement complete');
  } catch (error) {
    console.warn('⚠️ [Speaker] Error forcing loudspeaker:', error);
  }
};


export type ControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
};

type MediaStreamButtonProps = {
  isStreaming: boolean;
  onIcon: string;
  offIcon: string;
  start: () => Promise<any>;
  stop: () => any;
};

/**
 * button used for triggering webcam or screen-capture
 */
const MediaStreamButton = memo(
  ({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
    isStreaming ? (
      <button className="action-button" onClick={stop}>
        <span className="material-symbols-outlined">{onIcon}</span>
      </button>
    ) : (
      <button className="action-button" onClick={start}>
        <span className="material-symbols-outlined">{offIcon}</span>
      </button>
    )
);

// Constants for audio optimization
const VAD_THRESHOLD = 0.005; // Volume threshold for Voice Activity Detection (lowered for sensitivity)
const VAD_HOLD_MS = 300; // Hold speech state for 300ms after volume drops (prevents choppy cutoffs)
const IDLE_TIMEOUT_MS = 30000; // 30 seconds idle timeout

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => { },
  supportsVideo,
}: ControlTrayProps) {
  const webcam = useWebcam();
  const videoStreams = [webcam];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const [recorderReady, setRecorderReady] = useState(false); // Track when micGainNode is ready
  const [isSpeaking, setIsSpeaking] = useState(false); // VAD: track if user is speaking
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null); // Idle connection timer
  const lastActivityRef = useRef<number>(Date.now()); // Track last activity time

  // Refs for values that need to be current in audio callbacks (avoid stale closures)
  const mutedRef = useRef(muted);
  const isSpeakingRef = useRef(false);
  const speechHoldTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Vision-freeze: tracks whether vision processing has forced the mic off.
  // Separate from the user-controlled `muted` state so unfreeze only lifts
  // the forced mute, never overriding a deliberate user mute.
  const visionFrozenRef = useRef(false);

  // Use agent context instead of live API context
  const {
    connected,
    disconnect,
    volume,
    audioStreamer,
    sendRealtimeInput,
    connect,
    hasConfig,
    isConnecting,
    client,
  } = useAgentContext();

  // Keep disconnect in a ref so resetIdleTimer doesn't need it as a dependency.
  // disconnect from context is NOT a stable reference (recreated each render),
  // so using a ref prevents the idle timer useEffect from re-running on every render.
  const disconnectRef = useRef(disconnect);
  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);

  // Reset idle timer on any activity.
  // Depends ONLY on [connected] — disconnect is accessed via disconnectRef to avoid
  // re-creating this function on every render (disconnect from context is not stable).
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    if (connected) {
      idleTimerRef.current = setTimeout(() => {
        console.log('⏰ [Idle] No activity for 30 seconds, disconnecting...');
        disconnectRef.current();
      }, IDLE_TIMEOUT_MS);
    }
  }, [connected]); // ← only [connected], not disconnect — see disconnectRef above

  // Idle timeout management — only re-runs when connected changes.
  // resetIdleTimer identity is stable within a session (only changes when connected changes)
  // so this effect effectively runs exactly once on connect and once on disconnect.
  useEffect(() => {
    if (connected) {
      resetIdleTimer();
      console.log('⏰ [Idle] Started 30-second idle timer');
    } else {
      // Clear timer when disconnected
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      // Reset recorder ready state on disconnect
      setRecorderReady(false);
      setIsSpeaking(false);
    }

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]); // resetIdleTimer intentionally omitted: it only changes when connected changes (same trigger)

  // Force audio to loudspeaker on mobile devices
  useEffect(() => {
    if (connected && audioStreamer) {
      // Call loudspeaker enforcement when connected
      forceAudioToLoudspeaker();

      // Also force it whenever the audio context state changes
      const audioContext = audioStreamer.context;
      if (audioContext) {
        const handleStateChange = () => {
          console.log('🔊 [Speaker] AudioContext state changed to:', audioContext.state);
          if (audioContext.state === 'running') {
            forceAudioToLoudspeaker();
          }
        };

        audioContext.addEventListener('statechange', handleStateChange);

        return () => {
          audioContext.removeEventListener('statechange', handleStateChange);
        };
      }
    }
  }, [connected, audioStreamer]);

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  // Update activeVideoStream when webcam stream changes (e.g., after camera switching)
  useEffect(() => {
    if (webcam.isStreaming && webcam.stream) {
      console.log('📹 Updating video element with new webcam stream after camera switch');
      setActiveVideoStream(webcam.stream);
      onVideoStreamChange(webcam.stream);
    } else if (!webcam.isStreaming) {
      console.log('📹 Clearing video element - webcam stopped');
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }
  }, [webcam.stream, webcam.isStreaming, onVideoStreamChange]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`
    );
  }, [inVolume]);

  // Reset idle timer when AI audio is playing
  useEffect(() => {
    if (!connected || !client) return;

    const onAiAudio = () => {
      resetIdleTimer();
    };

    client.on('audio', onAiAudio);
    return () => {
      client.off('audio', onAiAudio);
    };
  }, [connected, client, resetIdleTimer]);

  // Handle audio recording - start once when connected
  // Mute uses GainNode (makes audio silent), idle timeout auto-disconnects
  useEffect(() => {
    const onData = (base64: string) => {
      // Only check mute - GainNode already handles making audio silent when muted
      // Removed VAD filtering as it caused latency issues due to timing mismatch
      if (mutedRef.current) {
        // Don't send anything when muted - save tokens
        return;
      }

      // Reset idle timer on any audio activity
      resetIdleTimer();

      sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };

    const onVolume = (vol: number) => {
      setInVolume(vol);

      // Track speaking state for UI feedback (visual indicator)
      // Not used for filtering audio anymore
      const speaking = vol > VAD_THRESHOLD;

      if (speaking) {
        if (speechHoldTimerRef.current) {
          clearTimeout(speechHoldTimerRef.current);
          speechHoldTimerRef.current = null;
        }
        isSpeakingRef.current = true;
        setIsSpeaking(true);
        resetIdleTimer();
      } else if (isSpeakingRef.current) {
        // Use hold timer for smooth UI transition
        if (!speechHoldTimerRef.current) {
          speechHoldTimerRef.current = setTimeout(() => {
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            speechHoldTimerRef.current = null;
          }, VAD_HOLD_MS);
        }
      }
    };

    // Start the recorder once when connected
    if (connected && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", onVolume);

      // Await start() to ensure micGainNode is created before applying mute
      audioRecorder.start().then(() => {
        console.log('🎤 [AudioRecorder] Started and ready');
        setRecorderReady(true);

        // Apply pending mute state now that micGainNode exists
        if (mutedRef.current) {
          audioRecorder.muteMic();
          console.log('🔇 [Mute] Applied pending mute state after recorder ready');
        }
      }).catch(err => {
        console.error('🎤 [AudioRecorder] Failed to start:', err);
      });

      // MOBILE FIX: Ensure audio output context stays active
      if (audioStreamer) {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          audioContext.resume().then(() => {
            console.log('🔊 [Mobile Fix] Audio output context resumed');
            if (audioStreamer.gainNode) {
              audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
              audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
            }
          }).catch(err => {
            console.warn('⚠️ [Mobile Fix] Failed to resume output context:', err);
          });
        }
      }
    } else if (!connected && audioRecorder) {
      // Only stop when disconnecting completely
      audioRecorder.stop();
      setRecorderReady(false);
      isSpeakingRef.current = false;

      // Clear speech hold timer on disconnect
      if (speechHoldTimerRef.current) {
        clearTimeout(speechHoldTimerRef.current);
        speechHoldTimerRef.current = null;
      }
    }

    return () => {
      audioRecorder.off("data", onData).off("volume", onVolume);
      // Clear speech hold timer on cleanup
      if (speechHoldTimerRef.current) {
        clearTimeout(speechHoldTimerRef.current);
        speechHoldTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, sendRealtimeInput, audioRecorder, audioStreamer]); // resetIdleTimer intentionally omitted: it changes identity when `connected` changes (same trigger as this effect), which would cause a double-start race. It is accessed via fresh closure on each effect run.

  // Separate useEffect for applying mute state changes after recorder is ready
  useEffect(() => {
    if (connected && recorderReady && audioRecorder && audioRecorder.micGainNode) {
      // Apply mute state via gain (no stream interruption)
      if (muted) {
        audioRecorder.muteMic();
        console.log('🔇 [Effect] Microphone muted via gain');
      } else {
        audioRecorder.unmuteMic();
        console.log('🔊 [Effect] Microphone unmuted via gain');
      }
    }
  }, [muted, connected, recorderReady, audioRecorder]); // Added recorderReady to ensure micGainNode exists

  // Vision freeze/unfreeze: forcefully mute mic while vision model is processing.
  // This prevents Azure VAD from creating new turns and the AI from speaking
  // until the vision description is ready.
  useEffect(() => {
    const onFreeze = () => {
      visionFrozenRef.current = true;
      mutedRef.current = true; // block the onData callback immediately
      if (audioRecorder?.micGainNode) {
        audioRecorder.muteMic();
        console.log('🔇 [VisionFreeze] Mic force-muted for vision processing');
      }
    };

    const onUnfreeze = () => {
      visionFrozenRef.current = false;
      // Always clear the hard block — vision is done, data must flow again.
      // Even if the user is manually muted, hardware gain = 0 means only silence
      // reaches Azure, which is fine: VAD sees silence → AI doesn't respond until
      // the user actually speaks. This keeps soft-mute behaviour intact.
      mutedRef.current = false;
      if (!muted && audioRecorder?.micGainNode) {
        audioRecorder.unmuteMic();
        console.log('🔊 [VisionUnfreeze] Mic unmuted after vision complete');
      } else if (muted) {
        console.log('🔇 [VisionUnfreeze] Vision complete — mic stays hardware-muted by user');
      }
    };

    window.addEventListener('vision:freeze', onFreeze);
    window.addEventListener('vision:unfreeze', onUnfreeze);
    return () => {
      window.removeEventListener('vision:freeze', onFreeze);
      window.removeEventListener('vision:unfreeze', onUnfreeze);
    };
  }, [audioRecorder, muted]);

  useEffect(() => {
    if (videoRef.current) {
      console.log('🎥 Setting video srcObject:', activeVideoStream ? 'New stream' : 'null');
      videoRef.current.srcObject = activeVideoStream;

      // Force video to load and play the new stream (important for Android)
      if (activeVideoStream) {
        videoRef.current.load();
        videoRef.current.play().catch((error) => {
          console.warn('📹 Video play failed (this is usually normal):', error);
        });
      }
    }

    let timeoutId = -1;
    // Track whether a vision request is already in-flight to avoid overlap
    let visionInFlight = false;

    async function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;

      if (canvas.width + canvas.height > 0 && !visionInFlight && connected) {
        ctx.drawImage(videoRef.current!, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 0.75).split(",")[1];

        visionInFlight = true;
        try {
          // Import lazily to avoid circular deps at module level
          const { analyzeLiveCameraFrame } = await import("../../services/visionService");

          // Detect language from page if possible (default to Arabic)
          const lang = (document.documentElement.lang as 'ar' | 'en' | 'ur') || 'ar';
          const backendUrl = config.REACT_APP_BACKEND_URL || '';

          const result = await analyzeLiveCameraFrame({
            backendUrl,
            images: [base64],
            mimeTypes: ["image/jpeg"],
            language: lang,
          });

          if (client && connected && result.context_message) {
            client.send([{ text: result.context_message }], false);
          }
        } catch (err) {
          console.warn('📷 [ControlTray] Vision frame analysis failed:', err);
        } finally {
          visionInFlight = false;
        }
      }

      if (connected) {
        // Throttle: analyze one frame every 4 seconds (vision API is not real-time)
        timeoutId = window.setTimeout(sendVideoFrame, 4000);
      }
    }

    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  // Handle mute/unmute with gain-based control (no stream interruption)
  const handleMuteToggle = async () => {
    // Reset idle timer on user interaction
    resetIdleTimer();

    const newMutedState = !muted;
    setMuted(newMutedState);

    // NOTE: mutedRef is intentionally NOT updated here.
    // mutedRef is ONLY for vision-freeze hard-blocking (prevents any audio during vision).
    // User mute is "soft" — hardware gain is zeroed but silent data still flows to Azure
    // so that server-side VAD can detect end-of-speech and trigger AI responses.
    // Updating mutedRef here would prevent Azure from seeing the silence, breaking VAD.

    // Only apply mute via gain if recorder is ready (micGainNode exists)
    if (recorderReady && audioRecorder.micGainNode) {
      if (newMutedState) {
        audioRecorder.muteMic();
        console.log('🔇 [Mute] Microphone muted via gain');
      } else if (!visionFrozenRef.current) {
        audioRecorder.unmuteMic();
        console.log('🔊 [Unmute] Microphone unmuted via gain');
      } else {
        console.log('🔊 [Unmute] User toggled unmute — mic stays gain-muted while vision is processing');
      }
    } else {
      console.log('⏳ [Mute] Recorder not ready yet, mute will be applied when ready');
    }

    // MOBILE FIX: Force audio to loudspeaker on every toggle
    await forceAudioToLoudspeaker();

    // MOBILE FIX: Ensure output audio context stays active and at full volume
    if (audioStreamer) {
      try {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          // Resume if suspended
          if (audioContext.state !== 'running') {
            console.log('🔊 [Mobile Fix] Resuming AudioContext via user interaction');
            await audioContext.resume();
          }

          // Force output gain to maximum
          if (audioStreamer.gainNode) {
            audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
            audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
            console.log('🔊 [Mobile Fix] Output gain locked to 1.0');
          }
        }
      } catch (error) {
        console.warn('⚠️ [Mobile Fix] Error in handleMuteToggle:', error);
      }
    }
  };

  return (
    <section className="control-tray">
      <canvas style={{ display: "none" }} ref={renderCanvasRef} />
      <nav className={cn("actions-nav", { disabled: !connected, hidden: !connected })}>
        <button
          className={cn("action-button mic-button")}
          onClick={handleMuteToggle}
        >
          {!muted ? (
            <span className="material-symbols-outlined filled">mic</span>
          ) : (
            <span className="material-symbols-outlined filled">mic_off</span>
          )}
        </button>

        {supportsVideo && (
          <>
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
            />
            {/* Camera switch button - only show when webcam is active and multiple cameras available */}
            {webcam.isStreaming && webcam.canSwitchCamera && (
              <button
                className={cn("action-button camera-switch", {
                  switching: webcam.isSwitching
                })}
                onClick={webcam.switchCamera}
                disabled={webcam.isSwitching}
                title={webcam.isSwitching
                  ? 'Switching camera...'
                  : `Switch to ${webcam.facingMode === 'user' ? 'back' : 'front'} camera`
                }
              >
                {webcam.isSwitching ? (
                  <span className="material-symbols-outlined rotating">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">
                    {webcam.facingMode === 'user' ? 'camera_rear' : 'camera_front'}
                  </span>
                )}
              </button>
            )}
          </>
        )}

        <div className="action-button no-action outlined">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>

        {children}
      </nav>

      <div className={cn("connection-container", { connected })}>
        <div className="connection-button-container">
          <button
            ref={connectButtonRef}
            className={cn("action-button connect-toggle", {
              connected,
              connecting: isConnecting
            })}
            onClick={() => {
              resetIdleTimer(); // Reset on user interaction
              if (connected) {
                disconnect();
              } else if (!isConnecting) {
                connect();
              }
            }}
            disabled={isConnecting}
          >
            {connected ? (
              <img src="/animationPause.gif" alt="" style={{ width: '90px', height: '90px', zIndex: 1 }} />
            ) : isConnecting ? (
              <span className="material-symbols-outlined filled rotating">sync</span>
            ) : (
              <span className="material-symbols-outlined filled">play_arrow</span>
            )}
            {/* <span className="material-symbols-outlined filled">
              {connected ? "pause" : "play_arrow"}
            </span> */}
          </button>
        </div>
        <span className="text-indicator">
          {isConnecting ? 'Connecting...' : 'Streaming'}
        </span>
      </div>
    </section>
  );
}

export default memo(ControlTray);
